import React, { useMemo, useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalHeader } from '@heroui/react';
import AirtableConfigurator from './AirtableConfigurator.jsx';
import AhrefsAccountConfigurator from './AhrefsAccountConfigurator.jsx';

export default function IntegrationConfigModal({
  isOpen,
  onClose,
  provider,
  account,
  workspaceId,
  onAccountUpdated,
  onAccountCreated,
}) {
  const [saving, setSaving] = useState(false);

  const providerName = provider?.name ?? account?.provider;
  const isCreate = !account?.id;

  const handleClose = () => {
    if (saving) {
      return;
    }
    setSaving(false);
    onClose?.();
  };

  const handleSave = async (payload) => {
    try {
      setSaving(true);
      const endpoint = account?.id ? `/api/integrations/accounts/${account.id}` : '/api/integrations/accounts';
      const method = account?.id ? 'PATCH' : 'POST';
      let bodyPayload = account?.id ? { ...payload } : {
        provider: providerName,
        workspaceId,
        ...payload,
      };
      if (providerName !== 'ahrefs') {
        bodyPayload = {
          ...bodyPayload,
          metadata: {
            ...(account?.metadata ?? {}),
            [providerName]: payload,
          },
        };
      }

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bodyPayload),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.error || `Failed to save configuration (${response.status})`);
      }
      const body = await response.json();
      const updatedAccount = body?.account;
      if (updatedAccount) {
        if (account?.id) {
          onAccountUpdated?.(updatedAccount);
        } else {
          onAccountCreated?.(updatedAccount);
        }
      }
      console.info(account?.id ? 'Configuration saved' : 'Integration connected');
      setSaving(false);
      onClose?.();
    } catch (error) {
      console.error('Unable to save configuration', error);
      setSaving(false);
    }
  };

  const content = useMemo(() => {
    if (!providerName) {
      return null;
    }
    switch (providerName) {
      case 'ahrefs':
        return (
          <AhrefsAccountConfigurator
            account={account}
            saving={saving}
            onSubmit={handleSave}
            onCancel={handleClose}
          />
        );
      case 'airtable':
        return (
          <AirtableConfigurator
            account={account}
            saving={saving}
            onSubmit={handleSave}
            onCancel={handleClose}
          />
        );
      default:
        return (
          <div className="space-y-3">
            <p className="text-sm text-default-500">
              This integration does not require additional configuration.
            </p>
          </div>
        );
    }
  }, [account, handleClose, handleSave, providerName, saving]);

  const modalTitle = provider?.title || provider?.name || account?.provider || 'Integration';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isDismissable={!saving}
      size="lg"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 text-lg font-semibold">
          {isCreate ? `Connect ${modalTitle}` : `Configure ${modalTitle}`}
        </ModalHeader>
        <ModalBody>
          {content}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
